// Audit log: who did what, when — every settings change, home entry, video view and export.
import { api } from "../lib/api";
import { fmt, has, t } from "../lib/i18n";
import { Link, route } from "../lib/router";
import { h, local, redraw } from "../lib/ui";
import { act, Button, isBusy, Select } from "../shared/components";
import { PageHead } from "./app";

const FILTERS = ["", "auth.", "user.", "mail.", "brand.", "settings.", "role.", "staff.", "home.", "member.", "ticket.", "estimate.", "visit.", "bodycam.", "field."];

function summary(row: any): string {
  const d = row.data;
  if (!d || typeof d !== "object") return "";
  if (row.action === "settings.update") return `${t(`settings.${row.entity_id}.label`)}: ${JSON.stringify(d.before)} → ${JSON.stringify(d.after)}`;
  if (row.action === "brand.update") return Object.keys(d).map((k) => t(`admin.audit.brandFields.${k}`)).join(", ");
  const keys = Object.keys(d).slice(0, 4);
  return keys.map((k) => `${k}: ${typeof d[k] === "object" ? JSON.stringify(d[k]) : d[k]}`).join(" · ").slice(0, 160);
}

export function AuditPage() {
  const home = route.query.get("home") ?? "";
  const s = local(`screen:audit.${home}`, () => ({ filter: "", rows: [] as any[], next: null as number | null, loaded: false, loadedFor: "" }));
  const load = (more: boolean) =>
    act("audit.load", async () => {
      const q = new URLSearchParams({ limit: "50" });
      if (s.filter) q.set("action", s.filter);
      if (home) q.set("home", home);
      if (more && s.next) q.set("before", String(s.next));
      const res = await api.get(`/api/admin/audit?${q}`);
      s.set({ rows: more ? [...s.rows, ...res.rows] : res.rows, next: res.next, loaded: true, loadedFor: s.filter });
    });
  if (!s.loaded || s.loadedFor !== s.filter) {
    if (!isBusy("audit.load")) queueMicrotask(() => load(false));
  }
  return (
    <div class="stack loose">
      <PageHead title={t("admin.audit.title")} intro={t("admin.audit.intro")} />
      <div class="row">
        <div style="width:260px">
          <Select value={s.filter} onChange={(v) => { s.filter = v; s.loaded = false; redraw(); }}
            options={FILTERS.map((f) => ({ value: f, label: f ? t(`admin.audit.filters.${f.slice(0, -1)}`) : t("admin.audit.filters.all") }))} />
        </div>
        {home ? <Link href="/admin/audit" class="btn secondary small">{t("admin.audit.clearHome")}</Link> : null}
      </div>
      <section class="panel">
        <div class="table-wrap">
          <table class="table">
            <thead><tr><th>{t("admin.audit.when")}</th><th>{t("admin.audit.who")}</th><th>{t("admin.audit.what")}</th><th>{t("fields.home")}</th><th>{t("admin.audit.details")}</th></tr></thead>
            <tbody>
              {s.rows.map((row) => (
                <tr key={row.id}>
                  <td class="nowrap small tabular">{fmt.dateTime(row.at)}</td>
                  <td class="small">{row.actor_name ?? <span class="muted">{t("admin.audit.system")}</span>}{row.ip ? <span class="sub">{row.ip}</span> : null}</td>
                  <td class="small strong">{has(`audit.${row.action}`) ? t(`audit.${row.action}`) : row.action}</td>
                  <td class="small">{row.home_code ?? "—"}</td>
                  <td class="xs muted" style="max-width:420px;word-break:break-word">{summary(row)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {!s.rows.length && s.loaded ? <div class="empty"><span>{t("admin.audit.empty")}</span></div> : null}
        {s.next ? <div class="panel-foot"><Button kind="secondary" busy={isBusy("audit.load")} onClick={() => load(true)}>{t("admin.audit.more")}</Button></div> : null}
      </section>
    </div>
  );
}
